import { useAuth0 } from "@auth0/auth0-react";
import {
  Chip,
  Drawer,
  Grid,
  IconButton,
  InputBase,
  Paper,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { RefetchContext } from "./refetchProvider";
import SearchIcon from "@mui/icons-material/Search";
import { CustomButton } from "./PersonalDetails";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import {
  DataGridPremium,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { formatPhoneNumber } from "./DatagridFunctions";
import { useQuery } from "@apollo/client";
import { GET_PROPERTIES } from "./v2-gql";
import { CustomChip } from "./individualView";
import { SidebarContacts } from "./v2Utils";
import CloseIcon from "@mui/icons-material/Close";
import { WorkspaceContext } from "./v2workspaceProvider";

interface RowData {
  id: number;
  propertyAddress: string;
  assessorOwner: string;
  ownerName: string;
  ownerPhone: string;
  ownerEmail: string;
  ownerMailingAddress: string;
}

const getColumns = (setOpenContacts: any) => [
  {
    field: "address",
    headerName: "Address",
    width: 300,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Link
            to={`property/${params.id}`}
            style={{ color: "#0000EE" }}
            state={{ row: params?.row }}
          >
            {params?.value}
          </Link>
        </div>
      );
    },
  },
  {
    field: "rba",
    headerName: "Building SF",
    width: 200,
    editable: true,
  },
  {
    field: "gisacre",
    headerName: "Land SF",
    width: 200,
    editable: true,
  },
  {
    field: "tags",
    headerName: "Tags",
    width: 200,
    renderCell: (params: GridRenderCellParams) => {
      return params?.value?.map((tag: any) => {
        return (
          <Chip
            variant="outlined"
            size="small"
            label={tag}
            sx={{ fontSize: "12px", marginRight: "8px" }}
          />
        );
      });
    },
  },
  {
    field: "contacts",
    headerName: "Contacts",
    width: 600,
    renderCell: (params: GridRenderCellParams) => {
      return params?.value?.map((contact: any, index: number) => {
        return (
          <CustomChip
            variant="outlined"
            onClick={() => {
              setOpenContacts({ contact: params?.value, index });
            }}
            size="small"
            label={contact?.contact?.name?.toLowerCase()}
            sx={{
              fontSize: "12px",
              marginRight: "8px",
              textTransform: "capitalize",
            }}
          />
        );
      });
    },
  },
  {
    field: "zoning",
    headerName: "Zoning",
    width: 200,
  },
  {
    field: "yearbuild",
    headerName: "Year Built",
    width: 200,
  },
];

export default function PropertiesSection() {
  const { user, getAccessTokenSilently } = useAuth0();

  const [filteredRows, setFilteredRows] = useState<any>([]);
  const [openContacts, setOpenContacts] = useState<any>(undefined);

  const workspaceContext = useContext(WorkspaceContext);

  const { loading, error, data } = useQuery(GET_PROPERTIES, {
    variables: {
      query: { workspaceId: workspaceContext?.selectedWorkspace?._id },
    },
    fetchPolicy: "cache-and-network",
  });
  const context = useContext(RefetchContext);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.toLowerCase();
    const newFilteredRows = data?.getProperties?.filter((row: any) => {
      return row?.address?.toLowerCase()?.includes(searchValue);
    });
    setFilteredRows(newFilteredRows);
  };

  useEffect(() => {
    if (data) {
      setFilteredRows([...data?.getProperties]?.reverse());
    }
  }, [data]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Grid
        container
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{
          width: "90%",
          //   maxWidth: "1048px",
          margin: "auto",
          marginTop: "12px",
          backgroundColor: "white",
          padding: "24px",
          borderRadius: "12px",
        }}
      >
        <Grid
          xs={6}
          sx={{ display: "flex", flexDirection: "row", gap: "12px" }}
        >
          <Paper
            // component="form"
            sx={{
              p: "0px",
              display: "flex",
              alignItems: "center",
              width: "250px !important",
              boxShadow: "none",
              border: "1px solid #d1d1d1",
              backgroundColor: "#f7f9fa",
            }}
          >
            <div style={{ padding: "4px 8px", display: "flex" }}>
              <SearchIcon sx={{ fontSize: "18px", color: "#2b70ef" }} />
            </div>
            <InputBase
              sx={{ ml: 1, flex: 1, fontSize: "14px" }}
              placeholder="Search Properties"
              inputProps={{ "aria-label": "search properties" }}
              onChange={handleSearch} // Listen for changes here
            />
            {/* <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" /> */}
          </Paper>
          <Tooltip title="Filter Contacts">
            <IconButton
              type="button"
              sx={{ p: "4px" }}
              aria-label="filter"
              onClick={() => {}}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid
          xs={6}
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <CustomButton
            sx={{
              fontSize: "14px",
              padding: "4px 8px",
            }}
          >
            <AddIcon sx={{ fontSize: "20px" }} />
            Add Property
          </CustomButton>
        </Grid>
        <Grid xs={12} sx={{ marginTop: "24px", height: "calc(100vh - 250px)" }}>
          <DataGridPremium
            autoPageSize
            pagination
            rowHeight={50}
            getRowId={(row) => row._id}
            rows={filteredRows}
            loading={loading}
            columns={getColumns(setOpenContacts)}
            initialState={
              {
                //   filter: {
                //     filterModel: {
                //       items: [],
                //       quickFilterValues: ["quick", "filter"],
                //     },
                //   },
              }
            }
            pageSizeOptions={[5]}
            checkboxSelection
            disableRowSelectionOnClick
          />
        </Grid>
      </Grid>
      <Drawer
        open={!!openContacts}
        onClose={() => setOpenContacts(undefined)}
        variant="persistent"
        anchor="right"
        sx={{
          width: 350,
          padding: "12px",
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            padding: "12px",
            width: 350,
            boxSizing: "border-box",
          },
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <Typography variant="body1">Related Contacts</Typography>
          <IconButton
            onClick={() => {
              setOpenContacts(undefined);
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <SidebarContacts openContacts={openContacts} />
      </Drawer>
    </>
  );
}
